import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import createOrderApi from 'api/order';
import createCarbonApi from 'api/carbonOffset';
import { currentUserSelector } from 'modules/user/userSlice';
import { AppThunk, RootState } from 'store';
import { appContextSelector } from 'modules/app/appSlice';
import { syncAttribution, setMychoooseCustomerCo2 } from 'modules/user/attributionSlice';
import { format, intervalToDuration, addMonths, isBefore, addDays, isAfter, parseISO } from 'date-fns';
import getPlan, { plans } from 'utils/planMapper';
import { Stripe } from '@stripe/stripe-js';
import { showToast } from 'modules/app/toasterSlice';
import { defaultPortfolioSelector } from 'modules/projects/projectsSlice';
import { setTag, tags } from 'utils/tags';

export const giftDateFormat = 'yyyy-MM-dd';

export type Steps = 'giftType' | 'summary' | 'success' | 'error';

export type CardType = 'halfYear' | 'oneYear' | 'custom';

interface State {
  country: string;
  startDate: string;
  endDate: string;
  plan: string;
  price: number;
  pricePreviews: {
    halfYear: number;
    oneYear: number;
    custom: number;
  };
  carbonPreviews: {
    halfYear: number;
    oneYear: number;
    custom: number;
  };
  vat?: number;
  currency: string;
  kilosCo2: number;
  step: Steps;
  orderNumber: string;
  orderDate: string;
  orderId: string;
  isLoading: boolean;
  orderName?: string;
  selectedCard?: CardType;
  selectedPlanId: string;
  nameOnCard: string;
  receivedGiftCards?: GiftCardData[];
}

interface GetPriceData {
  country: string;
  years: number;
  months: number;
  days: number;
  multiplier: number;
}

interface GetPriceProps {
  country?: string;
  selectedPlanId?: string;
  startDate?: string;
  endDate?: string;
}

interface OrderPayload {
  number: string;
  date: string;
  id: string;
}

interface OffsetPrice {
  price: number;
  currency: string;
  vat?: number;
}

const initialState: State = {
  country: 'NO',
  startDate: format(new Date(), 'yyyy-MM-dd'),
  endDate: format(new Date(new Date().getDate() + 1), 'yyyy-MM-dd'),
  plan: 'ClimatePositive',
  kilosCo2: 0,
  price: 0,
  pricePreviews: {
    halfYear: 0,
    oneYear: 0,
    custom: 0,
  },
  carbonPreviews: {
    halfYear: 0,
    oneYear: 0,
    custom: 0,
  },
  currency: '',
  step: 'giftType',
  orderNumber: '',
  orderDate: '',
  orderId: '',
  isLoading: false,
  selectedPlanId: 'ClimatePositive',
  nameOnCard: '',
  receivedGiftCards: [],
};

const slice = createSlice({
  name: 'gifts',
  initialState,
  reducers: {
    reset: (): State => {
      return { ...initialState };
    },
    resetWithoutStepAndCurrency: (state: State): State => {
      return { ...initialState, step: state.step, currency: state.currency };
    },
    setPrice: (state: State, action: PayloadAction<OffsetPrice>): State => {
      state.price = action.payload.price;
      state.currency = action.payload.currency;
      state.vat = action.payload.vat;
      return state;
    },
    setLoading: (state: State, action: PayloadAction<boolean>): State => {
      state.isLoading = action.payload;
      return state;
    },
    setStep: (state: State, action: PayloadAction<Steps>): State => {
      state.step = action.payload;
      return state;
    },
    setCo2: (state: State, action: PayloadAction<number>): State => {
      state.kilosCo2 = action.payload;
      return state;
    },
    setOrder: (state: State, action: PayloadAction<OrderPayload>): State => {
      state.orderNumber = action.payload.number;
      state.orderDate = action.payload.date;
      state.orderId = action.payload.id;
      return state;
    },
    setSelectedCard: (state: State, action: PayloadAction<CardType>): State => {
      state.selectedCard = action.payload;
      const { startDate, endDate, selectedCard } = state;
      switch (selectedCard) {
        case 'oneYear':
          state.endDate = format(addMonths(new Date(startDate), 12), giftDateFormat);
          break;
        case 'halfYear':
          state.endDate = format(addMonths(new Date(startDate), 6), giftDateFormat);
          break;
        default:
          if (!isAfter(new Date(endDate), new Date(startDate))) {
            state.endDate = format(addMonths(new Date(startDate), 1), giftDateFormat);
          }
          break;
      }

      return state;
    },
    setStartDate: (state: State, action: PayloadAction<number>): State => {
      const date = action.payload;
      state.startDate = format(date, giftDateFormat);

      switch (state.selectedCard) {
        case 'oneYear':
          state.endDate = format(addMonths(new Date(date), 12), giftDateFormat);
          break;
        case 'halfYear':
          state.endDate = format(addMonths(new Date(date), 16), giftDateFormat);
          break;
        default:
          if (!isAfter(new Date(state.endDate), date)) {
            state.endDate = format(addDays(date, 1), giftDateFormat);
          }
          break;
      }
      return state;
    },
    setEndDate: (state: State, action: PayloadAction<number>): State => {
      const date = action.payload;

      if (isBefore(date, new Date(state.startDate))) {
        state.endDate = format(addDays(date, 1), giftDateFormat);
        return state;
      }

      state.endDate = format(date, giftDateFormat);
      return state;
    },
    setSelectedPlanId: (state: State, action: PayloadAction<string>): State => {
      state.selectedPlanId = action.payload;
      return state;
    },
    setCountry: (state: State, action: PayloadAction<string>): State => {
      state.country = action.payload;
      return state;
    },
    setNameOnCard: (state: State, action: PayloadAction<string>): State => {
      state.nameOnCard = action.payload;
      return state;
    },

    setPricePreview: (state: State, action: PayloadAction<{ type: CardType; value: number }>): State => {
      const { type, value } = action.payload;
      state.pricePreviews[type] = value;
      return state;
    },
    setCarbonAmountPreview: (state: State, action: PayloadAction<{ type: CardType; value: number }>): State => {
      const { type, value } = action.payload;
      state.carbonPreviews[type] = value;
      return state;
    },

    setReceivedGiftcards: (state: State, action: PayloadAction<GiftCardData[]>): State => {
      state.receivedGiftCards = action.payload;
      return state;
    },
  },
});

const {
  setStep,
  reset,
  setLoading,
  resetWithoutStepAndCurrency,
  setOrder,
  setPrice,
  setSelectedCard,
  setSelectedPlanId,
  setCountry,
  setStartDate,
  setEndDate,
  setCo2,
  setNameOnCard,
  setPricePreview,
  setCarbonAmountPreview,
  setReceivedGiftcards,
} = slice.actions;

export { setStep, reset, setSelectedCard, setSelectedPlanId, setCountry, setStartDate, setEndDate, setNameOnCard, setReceivedGiftcards };

export default slice.reducer;

function getParamsFromPropsOrState(state: State, props?: GetPriceProps): GetPriceData {
  const { years, months, days } = intervalToDuration({
    start: new Date(props?.startDate ?? state.startDate),
    end: new Date(props?.endDate ?? state.endDate),
  });

  const plan = getPlan(props?.selectedPlanId ?? state.selectedPlanId);
  const multiplier = plan.multiplier || plans[0].multiplier;

  return {
    country: props?.country ?? state.country,
    years: years ?? 0,
    months: months ?? 0,
    days: days ?? 0,
    multiplier,
  };
}

export const getPreviewPriceForPeriod = (data: GetPriceData, type: CardType): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const { country, years, months, days, multiplier } = data;

  dispatch(setLoading(true));
  try {
    const perCapitaData = await carbonApi.getPercapitaFootprint({ persons: 1, years, months, days }, country);
    const kilosCo2 = perCapitaData.kilosCo2e * multiplier;
    const priceData = await carbonApi.getCarbonOffsetPrice({ kilosCo2, bulkCo2: true, percentageBulkCo2: true }, currentUser?.customerId ?? '');

    dispatch(setLoading(false));
    dispatch(
      setPricePreview({
        value: priceData.salesPrice,
        type,
      }),
    );
    dispatch(
      setCarbonAmountPreview({
        value: kilosCo2,
        type,
      }),
    );
  } catch (err) {
    // err
    dispatch(resetWithoutStepAndCurrency());
    dispatch(setStep('giftType'));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:fetchGiftPriceError' }));
  }
};

export const getPricePreviews = (props?: GetPriceProps): AppThunk => (dispatch, getState): void => {
  const params = getParamsFromPropsOrState(getState().giftCard, props);

  dispatch(getPreviewPriceForPeriod({ ...params, years: 0, months: 6, days: 0 }, 'halfYear'));
  dispatch(getPreviewPriceForPeriod({ ...params, years: 0, months: 12, days: 0 }, 'oneYear'));
  dispatch(getPreviewPriceForPeriod(params, 'custom'));
};

export const getOffsetPrice = (props?: GetPriceProps): AppThunk => async (dispatch, getState): Promise<void> => {
  const currentUser = currentUserSelector(getState());
  const context = appContextSelector(getState());
  const carbonApi = createCarbonApi(context);
  const { country, years, months, days, multiplier } = getParamsFromPropsOrState(getState().giftCard, props);

  dispatch(setLoading(true));

  try {
    const perCapitaData = await carbonApi.getPercapitaFootprint({ persons: 1, years, months, days }, country);
    const kilosCo2 = perCapitaData.kilosCo2e * multiplier;
    const priceData = await carbonApi.getCarbonOffsetPrice({ kilosCo2, bulkCo2: true, percentageBulkCo2: true }, currentUser?.customerId ?? '');

    dispatch(
      setPrice({
        price: priceData.salesPrice,
        currency: priceData.salesCurrency,
        vat: priceData.vat,
      }),
    );
    dispatch(setCo2(kilosCo2 / multiplier));
    dispatch(setLoading(false));
    // dispatch(setStep('giftType'));
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:fetchGiftPriceError' }));
    dispatch(resetWithoutStepAndCurrency());
    dispatch(setStep('giftType'));
  }
};

export const changeCountry = (country: string): AppThunk => (dispatch): void => {
  dispatch(setCountry(country));
  dispatch(getOffsetPrice({ country }));
  dispatch(getPricePreviews({ country }));
};

export const changePlan = (selectedPlanId: string): AppThunk => (dispatch): void => {
  dispatch(setSelectedPlanId(selectedPlanId));
  dispatch(getOffsetPrice({ selectedPlanId }));
  dispatch(getPricePreviews({ selectedPlanId }));
};

export const changeStartDate = (date: number): AppThunk => (dispatch, getState): void => {
  dispatch(setStartDate(date));
  const startDate = format(date, giftDateFormat);
  const { selectedCard, endDate: endDateFromState } = getState().giftCard;

  let endDate;
  switch (selectedCard) {
    case 'oneYear':
      endDate = format(addMonths(new Date(date), 12), giftDateFormat);
      break;
    case 'halfYear':
      endDate = format(addMonths(new Date(date), 16), giftDateFormat);
      break;
    default:
      if (isBefore(new Date(endDateFromState), date)) {
        endDate = format(addDays(date, 1), giftDateFormat);
      }
      break;
  }

  dispatch(getOffsetPrice({ startDate, endDate }));
  dispatch(getPricePreviews({ startDate, endDate }));
};

export const changeEndDate = (date: number): AppThunk => (dispatch, getState): void => {
  dispatch(setEndDate(date));

  const { startDate } = getState().giftCard;

  let endDate = format(date, giftDateFormat);

  if (isBefore(date, new Date(startDate))) {
    endDate = format(addDays(date, 1), giftDateFormat);
  }

  dispatch(getOffsetPrice({ startDate, endDate }));
  dispatch(getPricePreviews({ startDate, endDate }));
};

export const changeCard = (selectedCard: CardType): AppThunk => (dispatch, getState): void => {
  dispatch(setSelectedCard(selectedCard));
  const { startDate, endDate: endDateFromState } = getState().giftCard;
  let endDate = endDateFromState;
  switch (selectedCard) {
    case 'oneYear':
      endDate = format(addMonths(new Date(startDate), 12), giftDateFormat);
      break;
    case 'halfYear':
      endDate = format(addMonths(new Date(startDate), 6), giftDateFormat);
      break;
    default:
      if (isBefore(new Date(endDate), new Date(startDate))) {
        endDate = format(addDays(new Date(startDate), 1), giftDateFormat);
      }
      break;
  }
  dispatch(getOffsetPrice({ startDate, endDate }));
  dispatch(getPricePreviews({ startDate, endDate }));
};

export const createOrder = (stripe: Stripe | null, cb?: CallableFunction): AppThunk => async (dispatch, getState): Promise<void> => {
  dispatch(setLoading(true));
  if (!stripe) {
    dispatch(setStep('error'));
    return;
  }
  const context = appContextSelector(getState());
  const orderApi = createOrderApi(context);
  const { giftCard, user, attribution } = getState();
  const { currency, price, country, startDate, endDate, selectedPlanId, kilosCo2 } = giftCard;
  const { mychoooseCustomerCo2 } = attribution;
  const defaultPortfolio = defaultPortfolioSelector(getState());
  const previousGiftedCO2Amount = mychoooseCustomerCo2?.kilosCo2PaidForGiftedToOthers ?? 0;

  const orderPayload = {
    currency: currency ?? '',
    comment: '',
    description: `A gift order for ${price} ${currency}`,
    category: 'Carbon',
    items: [
      {
        type: 'impact',
        impact: 'co2',
        kilosCo2,
        price,
        portfolioId: defaultPortfolio?.portfolioData.id ?? 'defaultco2',
        feeFactors: {},
      },
    ],

    metadata: {
      gift: {
        country,
        startDate,
        endDate,
        plan: selectedPlanId,
      },
    },
    gift: true,
  };

  try {
    const orderResult = await orderApi.create(orderPayload, user.currentAccount?.customerId ?? '');

    if (orderResult.stripeClientSecret && orderResult.stripePaymentMethodId) {
      await stripe.confirmCardPayment(orderResult.stripeClientSecret, { payment_method: orderResult.stripePaymentMethodId });
    } else {
      throw new Error('No stripe data available');
    }
    setTag({
      ...tags.portalOffset.placeOrder,
      categories: [orderResult.order.category],
      transactionId: orderResult.orderId,
      transactionTotal: orderResult.order.totalPrice,
      currencyCode: orderResult.order.currency,
      transactionProducts: orderResult.order.items.map(item => ({
        category: orderResult.order.category,
        name: 'Gift card',
        price: item.price,
        quantity: item.kilosCo2,
        sku: 'gift-card',
      })),
    });
    dispatch(
      setOrder({
        id: orderResult.orderId,
        number: orderResult.order.choooseId,
        date: orderResult.order.createdDate,
      }),
    );

    dispatch(
      syncAttribution(() => {
        dispatch(setStep('success'));
        if (context === 'mychooose') {
          dispatch(setMychoooseCustomerCo2({ ...mychoooseCustomerCo2, kilosCo2PaidForGiftedToOthers: kilosCo2 + previousGiftedCO2Amount }));
        }
        dispatch(setLoading(false));
        cb && cb();
      }),
    );
  } catch (err) {
    dispatch(setLoading(false));
    dispatch(resetWithoutStepAndCurrency());
    dispatch(showToast({ variant: 'error', titlei18nKey: 'myOffset:errorTitle', descriptioni18nKey: 'myOffset:giftOrderError' }));
    dispatch(setStep('giftType'));
  }
};

export const priceTooLowSelector = createSelector(
  (state: RootState) => state.giftCard,
  ({ currency, pricePreviews, selectedCard }) => {
    if (!selectedCard) {
      return false;
    }
    const minimumOrderValues: { [key: string]: number } = {
      USD: 0.5,
      EUR: 0.5,
      GBP: 0.3,
      NOK: 3,
    };

    return pricePreviews[selectedCard] < minimumOrderValues[currency];
  },
);

export const saveGiftcards = (giftCards: GiftCardData[]): AppThunk => (dispatch): void => {
  const giftsSortedByCreatedDate = giftCards.sort((prev, next) => {
    if (isBefore(parseISO(prev.createdDate), parseISO(next.createdDate))) {
      return 1;
    } else {
      return -1;
    }
  });

  dispatch(setReceivedGiftcards(giftsSortedByCreatedDate.slice(-8)));
};
